(function () {
  const CONTAINER_ID = 'dynamic-cross-sell';
  const LIMIT = 8;
  const VISIBLE = 4;

  let lastProductId = null;
  let isRendered = false;
  let debounceTimer = null;

  // ── API ───────────────────────────────────────────────────────────

  async function fetchRecommendations(productId) {
    let res = await fetch(
      `/recommendations/products.json?product_id=${productId}&limit=${LIMIT}&intent=complementary`
    );
    let data = await res.json();
    if (!data.products || data.products.length === 0) {
      res = await fetch(
        `/recommendations/products.json?product_id=${productId}&limit=${LIMIT}&intent=related`
      );
      data = await res.json();
    }
    return data.products || [];
  }

  async function getSeedProductId() {
    const res = await fetch('/cart.js');
    const cart = await res.json();
    if (!cart.items || cart.items.length === 0) return null;
    return cart.items.reduce((a, b) =>
      a.final_line_price > b.final_line_price ? a : b
    ).product_id;
  }

  async function filterCartProducts(products) {
    const res = await fetch('/cart.js');
    const cart = await res.json();
    const cartProductIds = cart.items.map(i => i.product_id);
    return products.filter(p => !cartProductIds.includes(p.id));
  }

  async function addToCart(variantId, btn) {
    btn.textContent = 'Adding...';
    btn.disabled = true;
    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });
      btn.textContent = 'Added!';
      lastProductId = null; // force re-render after add
      isRendered = false;
      setTimeout(updateCrossSell, 600);
    } catch (e) {
      btn.textContent = 'Error';
      btn.disabled = false;
    }
  }

  // ── Helpers ───────────────────────────────────────────────────────

  function getImageUrl(p) {
    const imgRaw = (typeof p.featured_image === 'string')
      ? p.featured_image
      : p.featured_image?.src || p.images?.[0]?.src || p.images?.[0] || '';
    const img = imgRaw ? imgRaw.split('?')[0] : '';
    return img ? img.replace(/\.(jpg|jpeg|png|gif|webp)$/i, '_300x300.$1') : '';
  }

  function injectStyles() {
    if (document.getElementById('dcs-styles')) return;
    const style = document.createElement('style');
    style.id = 'dcs-styles';
    style.textContent = `
      .dcs-wrap {
        padding: 20px 0 4px;
        border-top: 1px solid rgba(0,0,0,0.1);
      }
      .dcs-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 12px;
      }
      .dcs-heading {
        font-size: 11px;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        font-weight: 600;
      }
      .dcs-arrow {
        background: none !important;
        border: 1px solid rgba(0,0,0,0.3) !important;
        border-radius: 50% !important;
        width: 28px !important;
        height: 28px !important;
        min-width: 0 !important;
        min-height: 0 !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        cursor: pointer !important;
        padding: 0 !important;
        flex-shrink: 0 !important;
        color: inherit !important;
        box-shadow: none !important;
        transition: border-color 0.2s, opacity 0.2s !important;
        line-height: 1 !important;
      }
      .dcs-arrow:hover { border-color: rgba(0,0,0,0.8) !important; }
      .dcs-arrow:disabled { opacity: 0.2 !important; cursor: default !important; }
      .dcs-arrow svg { display: block; pointer-events: none; }
      .dcs-track-wrap { overflow: hidden; }
      .dcs-track {
        display: flex;
        gap: 10px;
        transition: transform 0.35s ease;
        will-change: transform;
      }
      .dcs-card { flex: 0 0 calc(28.5% - 5px); min-width: 0; }
      .dcs-card__img-link {
        display: block;
        aspect-ratio: 1;
        overflow: hidden;
        background: #f4f4f4;
        margin-bottom: 7px;
        text-decoration: none;
      }
      .dcs-card__img-link img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
        transition: transform 0.3s ease;
      }
      .dcs-card__img-link:hover img { transform: scale(1.04); }
      .dcs-card__body { display: flex; flex-direction: column; gap: 3px; }
      .dcs-card__title {
        font-size: 10px;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        line-height: 1.3;
        color: inherit;
        text-decoration: none;
        display: block;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .dcs-card__title:hover { text-decoration: underline; }
      .dcs-card__price { font-size: 10px; opacity: 0.75; }
    `;
    document.head.appendChild(style);
  }

  // ── Render ────────────────────────────────────────────────────────

  function renderProducts(products) {
    const container = document.getElementById(CONTAINER_ID);
    if (!container) return;
    if (!products.length) { container.innerHTML = ''; return; }

    injectStyles();

    const total = products.length;

    const cardsHtml = products.map((p) => {
      const variant = p.variants[0];
      const price = `$${(variant.price / 100).toFixed(2)}`;
      const imgSrc = getImageUrl(p);
      return `
        <div class="dcs-card">
          <a href="/products/${p.handle}" class="dcs-card__img-link">
            ${imgSrc
              ? `<img src="${imgSrc}" alt="${p.title}" loading="lazy" />`
              : `<div style="width:100%;height:100%;background:#f0f0f0;"></div>`
            }
          </a>
          <div class="dcs-card__body">
            <a href="/products/${p.handle}" class="dcs-card__title">${p.title}</a>
            <span class="dcs-card__price">${price}</span>
          </div>
        </div>
      `;
    }).join('');

    const arrowSvg = `<svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6 3l5 5-5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;

    container.innerHTML = `
      <div class="dcs-wrap">
        <div class="dcs-header">
          <span class="dcs-heading">Complete with</span>
          ${total > VISIBLE
            ? `<button type="button" class="dcs-arrow dcs-arrow--next" aria-label="Next">${arrowSvg}</button>`
            : ''
          }
        </div>
        <div class="dcs-track-wrap">
          <div class="dcs-track">${cardsHtml}</div>
        </div>
      </div>
    `;

    isRendered = true;

    if (total <= VISIBLE) return;

    // ── Arrow ─────────────────────────────────────────────────────
    // Store index on the button element itself so re-renders don't
    // reset a closure variable
    const nextBtn = container.querySelector('.dcs-arrow--next');
    const track = container.querySelector('.dcs-track');
    nextBtn._index = 0;

    nextBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const card = container.querySelector('.dcs-card');
      if (!card || !track) return;

      const gap = 10;
      const cardWidth = card.getBoundingClientRect().width + gap;
      const maxIndex = total - VISIBLE;

      this._index = this._index + VISIBLE > maxIndex ? 0 : this._index + VISIBLE;
      track.style.transform = `translateX(-${this._index * cardWidth}px)`;
    });
  }

  // ── Main update ───────────────────────────────────────────────────

  async function updateCrossSell(force) {
    const productId = await getSeedProductId();

    // Skip re-render if same product and already rendered (unless forced)
    if (!force && isRendered && productId === lastProductId) return;

    lastProductId = productId;

    if (!productId) {
      const el = document.getElementById(CONTAINER_ID);
      if (el) el.innerHTML = '';
      isRendered = false;
      return;
    }

    let products = await fetchRecommendations(productId);
    products = await filterCartProducts(products);
    renderProducts(products);
  }

  // Debounced version — prevents rapid re-renders from MutationObserver
  function debouncedUpdate() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => updateCrossSell(), 250);
  }

  // ── Events ────────────────────────────────────────────────────────

  // Force re-render when cart items change
  ['cart:item-added', 'cart:updated'].forEach(evt => {
    document.addEventListener(evt, () => {
      isRendered = false;
      lastProductId = null;
      debouncedUpdate();
    });
  });

  // Soft open events — only render if not already shown
  ['cart:open', 'theme:cart:open'].forEach(evt => {
    document.addEventListener(evt, debouncedUpdate);
  });

  const cartDrawer = document.getElementById('cart-drawer');
  if (cartDrawer) {
    cartDrawer.addEventListener('open', debouncedUpdate);

    let wasOpen = false;
    const observer = new MutationObserver(() => {
      const isOpen =
        cartDrawer.hasAttribute('open') ||
        cartDrawer.hasAttribute('is-open') ||
        cartDrawer.classList.contains('is-open') ||
        cartDrawer.classList.contains('drawer--open');

      // Only trigger on the transition from closed → open
      if (isOpen && !wasOpen) {
        wasOpen = true;
        debouncedUpdate();
      } else if (!isOpen) {
        wasOpen = false;
      }
    });

    observer.observe(cartDrawer, {
      attributes: true,
      attributeFilter: ['open', 'is-open', 'class']
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    setTimeout(updateCrossSell, 300);
  });

})();